import React, { useEffect, useState } from "react";
import { Table, Button, Modal, message, Card, Input, Alert, Image } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  BarsOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import axios from "axios";
import InvestorDocForm from "./investorDocumentForm";
import dayjs from "dayjs";
import CommonPagination from "../../components/common/pagination";

const InvestorDoc = () => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [investorDoc, setInvestorDoc] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [openDetail, setOpenDetail] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [successMsg, setSuccessMsg] = useState(null);

  const fieldLabels = {
    year: "Year",
    type: "Type",
    title: "Name",
    image: "Image",
    file_url: "Document",
    created_at: "Created At",
    updated_at: "Updated At",
  };

  const typeLabels = {
    prospektus: "Prospektus",
    "annual-report": "Laporan Tahunan",
    "annual-fin-report": "Laporan Keuangan Tahunan",
    "rups-info": "Info RUPS",
    "stock-info": "Info Saham",
    "devidend-info": "Info Deviden",
  };

  const mapOrder = (order) => {
    if (order === "ascend") return "ASC";
    if (order === "descend") return "DESC";
    return "DESC";
  };

  const fetchInvestorDoc = async (pageNumber = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/investorDoc`, {
        params: {
          search: searchText,
          page: pageNumber,
          limit,
          sort: sortField,
          order: sortOrder,
        },
      });
      setInvestorDoc(res.data.data.rows || []);
      setTotal(res.data.data.count || 0);
    } catch (error) {
      console.error("FetchInvestorDoc error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvestorDoc(page);
  }, [page, searchText, sortField, sortOrder]);

  const handleSubmit = async (formData) => {
    try {
      if (editData) {
        await axios.put(`${BASE_URL}/api/investorDoc/${editData.id}`, formData);
        setSuccessMsg("InvestorDoc updated");
      } else {
        await axios.post(`${BASE_URL}/api/investorDoc`, formData);
        setSuccessMsg("InvestorDoc added");
      }
      setTimeout(() => setSuccessMsg(null), 3000);
      setOpen(false);
      setEditData(null);
      fetchInvestorDoc(1);
    } catch (err) {
      message.error("Failed to submit investorDoc");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/api/investorDoc/${id}`);
      setSuccessMsg("InvestorDoc deleted");
      setTimeout(() => setSuccessMsg(null), 3000);
      fetchInvestorDoc(page);
    } catch (err) {
      message.error("Failed to delete investorDoc");
    }
  };

  const columns = [
    {
      title: "No",
      render: (_, __, index) => (page - 1) * limit + index + 1,
    },
    { title: "Year", dataIndex: "year", sorter: true },
    {
      title: "Type",
      dataIndex: "type",
      sorter: true,
      render: (value) => typeLabels[value] || value || "-",
    },
    // { title: 'Title', dataIndex: 'title' },
    {
      title: "Image",
      dataIndex: "image",
      render: (image) => {
        return image ? (
          <img
            src={`${BASE_URL}${image}`}
            alt="management"
            width={60}
            style={{ objectFit: "cover", borderRadius: 4 }}
          />
        ) : (
          "No Image"
        );
      },
    },
    {
      title: "File",
      dataIndex: "file_url",
      render: (file_url) => {
        return file_url ? (
          <a
            href={`${BASE_URL}${file_url}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View File
          </a>
        ) : (
          "No file"
        );
      },
    },
    {
      title: "Actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            style={{ borderColor: "#ec4899", color: "#ec4899" }}
            icon={<BarsOutlined />}
            type="link"
            onClick={() => {
              setDetailData(record);
              setOpenDetail(true);
            }}
          />
          <Button
            style={{
              backgroundColor: "#ec4899",
              borderColor: "#ec4899",
              color: "#fff",
            }}
            icon={<EditOutlined />}
            type="link"
            onClick={() => {
              setEditData({
                ...record,
                image: [
                  {
                    uid: "-1",
                    name: "image.jpg",
                    status: "done",
                    url: `${BASE_URL}${record.image}`,
                  },
                ],
                file_url: [
                  {
                    uid: "-1",
                    name: "file",
                    status: "done",
                    url: `${BASE_URL}${record.file_url}`,
                  },
                ],
              });
              setOpen(true);
            }}
          />
          <Button
            style={{
              backgroundColor: "#374151",
              borderColor: "#374151",
              color: "#fff",
            }}
            icon={<DeleteOutlined />}
            type="link"
            onClick={() => {
              Modal.confirm({
                title:
                  "Are you sure you want to delete this Investor Document?",
                centered: true,
                okText: "Yes",
                okType: "danger",
                cancelText: "No",
                onOk: () => handleDelete(record.id),
              });
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <Card title="Investor Document">
      {successMsg && (
        <Alert
          message={successMsg}
          type="success"
          showIcon
          style={{
            marginBottom: 16,
            borderRadius: 8,
            backgroundColor: "#f6ffed",
            color: "#389e0d",
          }}
        />
      )}

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Input.Search
          placeholder="Search Investor Document.."
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
        />
        <Button
          style={{
            backgroundColor: "#ec4899",
            borderColor: "#ec4899",
            color: "#fff",
          }}
          onClick={() => {
            setEditData(null);
            setOpen(true);
          }}
        >
          + Add Investor Document
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={investorDoc}
        rowKey="id"
        loading={loading}
        pagination={false}
        style={{ marginTop: 20 }}
        onChange={(pagination, filters, sorter) => {
          if (sorter && sorter.field) {
            setSortField(sorter.field);
            setSortOrder(mapOrder(sorter.order));
          }
        }}
      />

      <CommonPagination
        current={page}
        pageSize={limit}
        total={total}
        onChange={(newPage) => setPage(newPage)}
      />

      <Modal
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditData(null);
        }}
        footer={null}
        destroyOnClose
      >
        <InvestorDocForm
          initialValues={editData}
          onSuccess={handleSubmit}
          editMode={!!editData}
        />
      </Modal>

      <Modal
        title="Investor Document Detail"
        open={openDetail}
        onCancel={() => setOpenDetail(false)}
        footer={[
          <Button
            key="close"
            style={{
              backgroundColor: "#ec4899",
              borderColor: "#ec4899",
              color: "#fff",
            }}
            onClick={() => setOpenDetail(false)}
          >
            Close
          </Button>,
        ]}
      >
        <Table
          dataSource={Object.entries(detailData ?? {})
            .filter(([key]) => key !== "id")
            .map(([key, value]) => {
              let field = fieldLabels[key] || key;
              let displayValue = value;

              if (key === "type") {
                displayValue = typeLabels[value] || value || "-";
              }

              if (key === "image" && value) {
                let src = null;
                if (Array.isArray(value)) {
                  src = value[0]?.url || value[0];
                } else if (typeof value === "object") {
                  src = value.url || "";
                } else if (typeof value === "string") {
                  src = value;
                }
                if (src && !src.startsWith("http")) src = `${BASE_URL}${src}`;
                displayValue = src ? (
                  <Image
                    src={src}
                    width={100}
                    style={{ borderRadius: 4 }}
                    preview={{
                      mask: (
                        <EyeOutlined style={{ color: "white", fontSize: 18 }} />
                      ),
                    }}
                  />
                ) : (
                  "-"
                );
              }

              if (key === "file_url" && value) {
                let fileSrc = null;
                if (Array.isArray(value)) {
                  fileSrc = value[0]?.url || value[0];
                } else if (typeof value === "object") {
                  fileSrc = value.url || "";
                } else if (typeof value === "string") {
                  fileSrc = value;
                }
                if (fileSrc && !fileSrc.startsWith("http"))
                  fileSrc = `${BASE_URL}${fileSrc}`;
                displayValue = fileSrc ? (
                  <a href={fileSrc} target="_blank" rel="noopener noreferrer">
                    View File
                  </a>
                ) : (
                  "-"
                );
              }

              if ((key === "created_at" || key === "updated_at") && value) {
                displayValue = dayjs(value).format("YYYY-MM-DD HH:mm:ss");
              }

              if (!displayValue) displayValue = "-";
              return { key, field, value: displayValue };
            })}
          columns={[
            {
              title: "Field",
              dataIndex: "field",
              key: "field",
              width: "30%",
              render: (text) => <strong>{text}</strong>,
            },
            { title: "Value", dataIndex: "value", key: "value" },
          ]}
          pagination={false}
          bordered
        />
      </Modal>
    </Card>
  );
};

export default InvestorDoc;
